import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/images';

export default function AboutPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Om os - Health4media.dk';
    let isMounted = true;

    async function loadTeam() {
      try {
        const data = await fetchCollection('team_members');
        if (isMounted) setTeam(data || []);
      } catch (err) {
        console.error('Error loading team:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadTeam();
    return () => { isMounted = false; };
  }, []);

  const coreValues = [
    {
      title: 'Sundhedsfaglig etik & præcision',
      desc: 'Fuld respekt for tavshedspligt, faglig etik og saglig information.',
      icon: 'fa-shield-halved'
    },
    {
      title: 'Tryghed foran kameraet',
      desc: 'Tryg vejledning, så fremtoningen bliver rolig, naturlig og sympatisk.',
      icon: 'fa-user-nurse'
    },
    {
      title: 'Effektiv tidsanvendelse',
      desc: 'I filmer i eget tempo når det passer jer – ingen forstyrrende optagedage i klinikken.',
      icon: 'fa-clock'
    },
    {
      title: 'Faglig formidling i øjenhøjde',
      desc: 'Omsætter kompliceret viden til letforståelige pointer for patienterne.',
      icon: 'fa-comments'
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-slate-950 text-white py-10 sm:py-16 md:py-24 border-b border-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/hero-modern-healthcare-consultation.png', { width: 1600, quality: 80, format: 'auto' })}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/75 to-slate-950/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Professionelt content, der skaber tillid, synlighed & patienttilgang
            </h1>
            <p className="mt-3 sm:mt-4 text-sm sm:text-lg md:text-xl text-slate-300 leading-relaxed">
              Vi hjælper sundhedsklinikker og behandlere med at opbygge stærk patienttillid, opnå massiv lokal synlighed og tiltrække nye patienter gennem autentisk videokommunikation og en gennemtænkt digital tilstedeværelse.
            </p>
          </div>
        </div>
      </section>

      {/* STORY & MISSION SECTION */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            <div className="order-2 lg:order-1 lg:col-span-7 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                Et fleksibelt samarbejde tilpasset klinikkens hverdag
              </h2>
              <p className="text-slate-600 leading-relaxed text-base">
                I sundhedsbranchen er tillid den vigtigste valuta. Patienter vælger ikke en klinik ud fra smarte salgstrick, men ud fra faglig tryghed, ro og menneskelig empati.
              </p>
              <p className="text-slate-600 leading-relaxed text-base">
                Vores service foregår 100% online, hvor vi vejleder jer til nem billedtagning og videooptagelse med mobilen i hverdagen. Vi optager ikke selv indholdet for jer – I sender blot råmaterialet ind, hvorefter vi klipper, redigerer og klargør det færdige content med professionelle undertekster og grafik.
              </p>
              <p className="text-slate-600 leading-relaxed text-base">
                Vi har desuden stor erfaring med at oprette og optimere sociale medieprofiler fra bunden, samt oprette og vedligeholde en overskuelig Meta SoMe postkalender, så jeres klinik opnår stabil, lokal synlighed uden tidsspild.
              </p>
              <p className="text-slate-600 leading-relaxed text-base">
                Hos Health4media tilpasser vi altid forløbet individuelt til jeres praksis og faglige ambitioner. Vi prioriterer et tæt og tillidsfuldt samarbejde med fuld fleksibilitet, så I kan fokusere på jeres patienter, mens vi sørger for en skarp og vedvarende digital tilstedeværelse.
              </p>
              
              <div className="pt-4 border-t border-slate-100 flex items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-sky-700 transition"
                >
                  <span>Book en samtale med os</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="order-1 lg:order-2 lg:col-span-5 h-full">
              <img
                src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/om-os-health4media.png', { width: 1000, quality: 85 })}
                alt="Samarbejde og strategi om sundhedsvideo"
                className="w-full h-full min-h-[320px] sm:min-h-[380px] lg:min-h-[420px] object-cover block shadow-xs border border-slate-200"
              />
            </div>

          </div>
        </div>
      </section>

      {/* CORE PILLARS / VALUES */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Det trygge fundament for vores samarbejde
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mt-3">
              Fire ufravigelige principper vi arbejder ud fra ved alle sundhedsprojekter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((v, idx) => (
              <div
                key={idx}
                className="space-y-2.5"
              >
                <div className="text-2xl text-sky-600 mb-3">
                  <i className={`fa-solid ${v.icon}`} aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  {v.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-20 sm:py-28 bg-slate-950 text-white text-center">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/shortform-phone-filming.png', { width: 1920, quality: 85 })}
            alt="Strategisk videoproduktion i sundhedsklinik"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-slate-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/60" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mb-4">
            Lad os tale om jeres sundhedskommunikation
          </h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-8 text-sm sm:text-base leading-relaxed">
            Udfyld formularen eller book en uforpligtende samtale, så vender vi hurtigt tilbage.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold transition shadow-lg shadow-sky-950/40 hover:-translate-y-0.5"
          >
            <span>Book en samtale</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>
      </section>

    </div>
  );
}
